
import { AIModule } from '../../types';

export const GAMING_AI_MODULE_1: AIModule = {
  id: 'gaming-ai-fundamentals',
  title: 'Gaming AI Fundamentals',
  description: 'Introduction to artificial intelligence in game development and interactive entertainment.',
  duration: '3 hours',
  difficulty: 'medium',
  content: `
# Gaming AI Fundamentals

Artificial Intelligence in gaming creates intelligent, responsive, and engaging virtual characters and game systems that enhance player experience and create dynamic, adaptive gameplay.

## What is Gaming AI?

Gaming AI refers to the application of artificial intelligence techniques to create intelligent behavior in video games. This includes:

- **Non-Player Characters (NPCs)**: Creating believable AI opponents and allies
- **Procedural Content Generation**: Automatically creating game levels, quests, and content
- **Adaptive Difficulty**: Adjusting game difficulty based on player skill
- **Player Analytics**: Understanding player behavior and preferences
- **Dynamic Storytelling**: Creating responsive narratives that adapt to player choices

## Core Gaming AI Concepts

### 1. Finite State Machines (FSM)
Simple AI system where characters have defined states and transitions.

**States Examples:**
- Idle
- Patrol
- Chase
- Attack
- Flee

### 2. Behavior Trees
Hierarchical AI system that allows complex decision-making.

**Node Types:**
- **Composite Nodes**: Control flow (Sequence, Selector, Parallel)
- **Decorator Nodes**: Modify child behavior (Repeat, Invert)
- **Leaf Nodes**: Actions and conditions

### 3. Pathfinding Algorithms
Finding optimal routes for character movement.

**A* Algorithm**: Most common pathfinding algorithm in games
**Dijkstra's Algorithm**: Shortest path algorithm
**Navigation Meshes**: Simplified representation of walkable areas

## Simple Game AI Implementation

\`\`\`python
import random
import math
from enum import Enum
from typing import List, Tuple

class AIState(Enum):
    IDLE = "idle"
    PATROL = "patrol"
    CHASE = "chase"
    ATTACK = "attack"
    FLEE = "flee"

class Vector2D:
    def __init__(self, x: float, y: float):
        self.x = x
        self.y = y
    
    def distance_to(self, other: 'Vector2D') -> float:
        return math.sqrt((self.x - other.x)**2 + (self.y - other.y)**2)
    
    def normalize(self) -> 'Vector2D':
        magnitude = math.sqrt(self.x**2 + self.y**2)
        if magnitude > 0:
            return Vector2D(self.x / magnitude, self.y / magnitude)
        return Vector2D(0, 0)
    
    def __add__(self, other: 'Vector2D') -> 'Vector2D':
        return Vector2D(self.x + other.x, self.y + other.y)
    
    def __mul__(self, scalar: float) -> 'Vector2D':
        return Vector2D(self.x * scalar, self.y * scalar)

class SimpleGameAI:
    def __init__(self, position: Vector2D, name: str = "AI"):
        self.position = position
        self.name = name
        self.state = AIState.IDLE
        self.health = 100
        self.max_health = 100
        self.speed = 2.0
        self.attack_range = 30
        self.vision_range = 100
        self.patrol_points = []
        self.current_patrol_index = 0
        self.target = None
        self.last_seen_player_position = None
        
        # AI Parameters
        self.aggression = 0.7  # 0.0 = passive, 1.0 = aggressive
        self.courage = 0.5     # 0.0 = cowardly, 1.0 = brave
        
    def add_patrol_point(self, point: Vector2D):
        """Add a patrol point for the AI to visit"""
        self.patrol_points.append(point)
    
    def can_see_target(self, target_position: Vector2D) -> bool:
        """Check if AI can see the target"""
        distance = self.position.distance_to(target_position)
        return distance <= self.vision_range
    
    def is_in_attack_range(self, target_position: Vector2D) -> bool:
        """Check if target is within attack range"""
        distance = self.position.distance_to(target_position)
        return distance <= self.attack_range
    
    def move_towards(self, target_position: Vector2D, delta_time: float):
        """Move AI towards target position"""
        direction = Vector2D(
            target_position.x - self.position.x,
            target_position.y - self.position.y
        ).normalize()
        
        movement = direction * self.speed * delta_time
        self.position = self.position + movement
    
    def update_idle_state(self, player_position: Vector2D, delta_time: float):
        """Handle idle state logic"""
        if self.can_see_target(player_position):
            self.target = player_position
            self.last_seen_player_position = player_position
            
            # Decide whether to chase or flee based on health and courage
            health_ratio = self.health / self.max_health
            if health_ratio > self.courage and self.aggression > 0.3:
                self.state = AIState.CHASE
            elif health_ratio < 0.3:
                self.state = AIState.FLEE
        elif self.patrol_points:
            self.state = AIState.PATROL
    
    def update_patrol_state(self, player_position: Vector2D, delta_time: float):
        """Handle patrol state logic"""
        if self.can_see_target(player_position):
            self.target = player_position
            self.last_seen_player_position = player_position
            self.state = AIState.CHASE
            return
        
        if not self.patrol_points:
            self.state = AIState.IDLE
            return
        
        current_target = self.patrol_points[self.current_patrol_index]
        distance = self.position.distance_to(current_target)
        
        if distance < 10:  # Reached patrol point
            self.current_patrol_index = (self.current_patrol_index + 1) % len(self.patrol_points)
        else:
            self.move_towards(current_target, delta_time)
    
    def update_chase_state(self, player_position: Vector2D, delta_time: float):
        """Handle chase state logic"""
        if self.can_see_target(player_position):
            self.target = player_position
            self.last_seen_player_position = player_position
            
            if self.is_in_attack_range(player_position):
                self.state = AIState.ATTACK
            else:
                self.move_towards(player_position, delta_time)
        elif self.last_seen_player_position:
            # Move to last known position
            if self.position.distance_to(self.last_seen_player_position) < 10:
                self.last_seen_player_position = None
                self.state = AIState.IDLE
            else:
                self.move_towards(self.last_seen_player_position, delta_time)
        else:
            self.state = AIState.IDLE
    
    def update_attack_state(self, player_position: Vector2D, delta_time: float):
        """Handle attack state logic"""
        if self.is_in_attack_range(player_position):
            # Perform attack
            self.attack_target()
            
            # Check if should continue attacking or flee
            health_ratio = self.health / self.max_health
            if health_ratio < 0.2:
                self.state = AIState.FLEE
        elif self.can_see_target(player_position):
            self.state = AIState.CHASE
        else:
            self.state = AIState.IDLE
    
    def update_flee_state(self, player_position: Vector2D, delta_time: float):
        """Handle flee state logic"""
        # Move away from player
        direction = Vector2D(
            self.position.x - player_position.x,
            self.position.y - player_position.y
        ).normalize()
        
        flee_position = self.position + (direction * 100)
        self.move_towards(flee_position, delta_time)
        
        # Stop fleeing if far enough away or health recovered
        distance = self.position.distance_to(player_position)
        health_ratio = self.health / self.max_health
        
        if distance > self.vision_range * 1.5 or health_ratio > 0.7:
            self.state = AIState.IDLE
    
    def attack_target(self):
        """Perform attack action"""
        damage = random.randint(10, 20)
        print(f"{self.name} attacks for {damage} damage!")
        return damage
    
    def take_damage(self, damage: int):
        """AI takes damage"""
        self.health = max(0, self.health - damage)
        print(f"{self.name} takes {damage} damage! Health: {self.health}")
        
        # Might trigger flee state if health is low
        if self.health < 30 and self.state != AIState.FLEE:
            self.state = AIState.FLEE
    
    def update(self, player_position: Vector2D, delta_time: float):
        """Main AI update loop"""
        state_handlers = {
            AIState.IDLE: self.update_idle_state,
            AIState.PATROL: self.update_patrol_state,
            AIState.CHASE: self.update_chase_state,
            AIState.ATTACK: self.update_attack_state,
            AIState.FLEE: self.update_flee_state
        }
        
        handler = state_handlers.get(self.state)
        if handler:
            handler(player_position, delta_time)
    
    def get_status(self) -> dict:
        """Get current AI status"""
        return {
            'name': self.name,
            'position': (self.position.x, self.position.y),
            'state': self.state.value,
            'health': self.health,
            'target': (self.target.x, self.target.y) if self.target else None
        }

# Example usage and simulation
def simulate_game_ai():
    """Simulate a simple game AI scenario"""
    # Create AI character
    ai = SimpleGameAI(Vector2D(0, 0), "Guard")
    
    # Add patrol points
    ai.add_patrol_point(Vector2D(0, 0))
    ai.add_patrol_point(Vector2D(100, 0))
    ai.add_patrol_point(Vector2D(100, 100))
    ai.add_patrol_point(Vector2D(0, 100))
    
    # Simulate player movement
    player_position = Vector2D(200, 200)
    
    print("=== Game AI Simulation ===")
    
    for frame in range(20):
        # Update AI
        ai.update(player_position, 1.0)  # 1 second per frame
        
        # Move player closer occasionally
        if frame % 5 == 0:
            player_position.x -= 20
            player_position.y -= 20
        
        # Print status
        status = ai.get_status()
        print(f"Frame {frame}: {status['name']} at ({status['position'][0]:.1f}, {status['position'][1]:.1f}) - {status['state']}")
        
        # Simulate combat
        if ai.state == AIState.ATTACK:
            ai.attack_target()
            ai.take_damage(15)  # Player fights back

# Run simulation
simulate_game_ai()
\`\`\`

## AI Techniques in Different Game Genres

### Action Games
- **Real-time decision making**
- **Combat AI with tactics**
- **Spatial reasoning for positioning**
- **Group coordination for team-based AI**

### Strategy Games
- **Resource management AI**
- **Long-term planning algorithms**
- **Economic optimization**
- **Military tactics and formations**

### RPG Games
- **Dynamic quest generation**
- **Character personality systems**
- **Adaptive dialogue systems**
- **Player choice consequences**

### Racing Games
- **Racing line optimization**
- **Opponent behavior modeling**
- **Rubber band AI for difficulty**
- **Physics-based driving AI**

## AI Design Principles

### 1. Player-Centric AI
- AI should enhance player experience
- Avoid being too difficult or too easy
- Provide clear feedback for AI actions

### 2. Believable Behavior
- AI should act consistently with character
- Avoid robotic or predictable patterns
- Include realistic mistakes and reactions

### 3. Performance Optimization
- AI should not impact game performance
- Use efficient algorithms and data structures
- Implement level-of-detail for AI complexity

### 4. Scalability
- AI systems should work with varying numbers of characters
- Design for different hardware capabilities
- Support modding and customization

Gaming AI is a fascinating field that combines computer science, psychology, and game design to create memorable and engaging interactive experiences.
  `,
  quiz: {
    id: 'gaming-ai-fundamentals-quiz',
    title: 'Gaming AI Fundamentals Quiz',
    passThreshold: 80,
    questions: [
      {
        id: 'q1',
        question: 'What is the main purpose of behavior trees in game AI?',
        type: 'multiple-choice',
        options: [
          'To store game data',
          'To create hierarchical decision-making systems',
          'To render graphics',
          'To handle network communication'
        ],
        correctAnswer: 1,
        explanation: 'Behavior trees provide a hierarchical structure for AI decision-making, allowing complex behaviors to be built from simple components.',
        difficulty: 'medium',
        points: 15
      },
      {
        id: 'q2',
        question: 'What is the A* algorithm primarily used for in games?',
        type: 'multiple-choice',
        options: [
          'Animation blending',
          'Pathfinding and navigation',
          'Texture mapping',
          'Sound processing'
        ],
        correctAnswer: 1,
        explanation: 'A* is a popular pathfinding algorithm used to find the optimal path for characters to move from one point to another in game worlds.',
        difficulty: 'medium',
        points: 15
      }
    ]
  },
  resources: [
    {
      id: 'gaming-ai-video',
      title: 'Gaming AI Introduction',
      type: 'video',
      url: 'https://example.com/gaming-ai',
      description: 'Comprehensive guide to AI in game development'
    }
  ]
};
